import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { UpdateOne_CancelReasons_Data } from '../../Redux/Actions/CancelReasons_Actions';
import { Notify } from '../../Utility/Notify';

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconEdit = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const IconTrash = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);

const IconWarning = () => (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IconClose = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IconCheck = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

/* ─── CSS ────────────────────────────────────────────────────────────────── */
const CSS = `
  .ra-btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; border-radius: 8px;
    border: 1.5px solid transparent; cursor: pointer;
    background: transparent; text-decoration: none;
    transition: all 0.18s ease; color: #7B829A;
  }
  .ra-btn:hover { transform: translateY(-1px); }
  .ra-btn.edit  { border-color: #e2e8f0; background: #f8fafc; color: #2563eb; }
  .ra-btn.edit:hover  { background: #eff6ff; border-color: #2563eb; }
  .ra-btn.del   { border-color: #fee2e2; background: #fff5f5; color: #ef4444; }
  .ra-btn.del:hover   { background: #fee2e2; border-color: #ef4444; }

  .dark-ra-edit { border-color: #2d3248 !important; background: #1e2132 !important; color: #3b82f6 !important; }
  .dark-ra-edit:hover { background: #1a2a4a !important; border-color: #3b82f6 !important; }
  .dark-ra-del  { border-color: #3a2525 !important; background: #2a1a1a !important; color: #f87171 !important; }
  .dark-ra-del:hover  { background: #3d1f1f !important; border-color: #f87171 !important; }

  .ra-overlay {
    position: fixed; inset: 0; z-index: 999999;
    display: flex; align-items: center; justify-content: center;
    background: rgba(0,0,0,0.70);
    backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
    animation: ra-fadein 0.2s ease-out both; padding: 20px;
  }
  @keyframes ra-fadein { from { opacity: 0; } to { opacity: 1; } }

  .ra-card {
    width: 100%; max-width: 460px;
    border-radius: 24px; padding: 40px 36px 36px;
    background: #ffffff;
    border: 1px solid #e8eaf0;
    box-shadow: 0 32px 80px rgba(0,0,0,0.22);
    position: relative; text-align: center;
    animation: ra-slidein 0.28s cubic-bezier(0.34,1.15,0.64,1) both;
    font-family: 'DM Sans', 'Segoe UI', sans-serif;
  }
  .ra-card.dark-m { background: #1e2132; border-color: #2a2f45; box-shadow: 0 32px 80px rgba(0,0,0,0.55); }
  @keyframes ra-slidein {
    from { opacity: 0; transform: translateY(24px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .ra-close {
    position: absolute; top: 16px; right: 16px;
    width: 34px; height: 34px; border-radius: 10px;
    border: 1.5px solid #e2e8f0; background: #f8fafc;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    color: #64748b; transition: all 0.2s;
  }
  .ra-close:hover { background: #fee2e2; border-color: #ef4444; color: #ef4444; transform: scale(1.05); }
  .dark-m .ra-close { background: #252d45; border-color: #2d3248; color: #9ca3af; }
  .dark-m .ra-close:hover { background: #3d1f1f; border-color: #f87171; color: #f87171; }

  .ra-icon-wrap {
    width: 80px; height: 80px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 20px;
  }
  .ra-icon-wrap.danger {
    background: linear-gradient(135deg, #fff1f2, #fee2e2);
    border: 2px solid #fca5a5; color: #ef4444;
    box-shadow: 0 8px 24px rgba(239,68,68,0.16);
  }
  .ra-icon-wrap.success-i {
    background: linear-gradient(135deg, #f0fdf4, #dcfce7);
    border: 2px solid #86efac; color: #16a34a;
    box-shadow: 0 8px 24px rgba(22,163,74,0.16);
    animation: ra-pulse 0.4s ease both;
  }
  .dark-m .ra-icon-wrap.danger  { background: linear-gradient(135deg,#2a1a1a,#3a2020); border-color: #7f1d1d; }
  .dark-m .ra-icon-wrap.success-i { background: linear-gradient(135deg,#052e16,#14532d); border-color: #166534; }
  @keyframes ra-pulse { 0%{transform:scale(0.75);opacity:0} 60%{transform:scale(1.08)} 100%{transform:scale(1);opacity:1} }

  .ra-title { font-size: 20px; font-weight: 700; letter-spacing: -0.3px; color: #0f172a; margin: 0 0 8px; }
  .dark-m .ra-title { color: #f1f5f9; }
  .ra-title.success-color { color: #16a34a !important; }
  .dark-m .ra-title.success-color { color: #4ade80 !important; }

  .ra-sub { font-size: 13.5px; color: #64748b; margin: 0 0 22px; line-height: 1.65; }
  .dark-m .ra-sub { color: #6b7280; }

  .ra-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 14px; border-radius: 40px;
    background: #fff1f2; border: 1.5px solid #fca5a5;
    font-size: 13px; font-weight: 600; color: #ef4444; margin-bottom: 22px;
  }
  .dark-m .ra-badge { background: #2a1a1a; border-color: #7f1d1d; color: #fca5a5; }

  .ra-divider { height: 1px; background: #e2e8f0; margin: 0 0 24px; }
  .dark-m .ra-divider { background: #2a2f45; }

  .ra-actions { display: flex; gap: 10px; }

  .ra-cancel {
    flex: 1; padding: 13px 0; border-radius: 12px;
    border: 1.5px solid #e2e8f0; background: #f8fafc;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 700;
    letter-spacing: 0.06em; text-transform: uppercase;
    color: #475569; cursor: pointer; transition: all 0.2s;
  }
  .ra-cancel:hover { background: #f1f5f9; border-color: #94a3b8; transform: translateY(-1px); }
  .dark-m .ra-cancel { background: #252d45; border-color: #2d3248; color: #9ca3af; }
  .dark-m .ra-cancel:hover { background: #2e3860; border-color: #3b82f6; color: #e2e8f0; }

  .ra-confirm {
    flex: 1; padding: 13px 0; border-radius: 12px; border: none;
    background: linear-gradient(135deg, #dc2626, #b91c1c);
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 700;
    letter-spacing: 0.06em; text-transform: uppercase;
    color: #fff; cursor: pointer; transition: all 0.2s;
    box-shadow: 0 4px 16px rgba(220,38,38,0.32);
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .ra-confirm:hover { background: linear-gradient(135deg,#ef4444,#dc2626); box-shadow: 0 6px 22px rgba(220,38,38,0.42); transform: translateY(-1px); }
  .ra-confirm:disabled { background: #e2e8f0; color: #94a3b8; box-shadow: none; transform: none; cursor: not-allowed; }
  .dark-m .ra-confirm:disabled { background: #252d45; color: #4b5563; }

  .ra-spin {
    width: 15px; height: 15px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: #fff; border-radius: 50%;
    animation: ra-spin 0.65s linear infinite; flex-shrink: 0;
  }
  .ra-spin-green {
    width: 28px; height: 28px;
    border: 3px solid rgba(22,163,74,0.2);
    border-top-color: #16a34a; border-radius: 50%;
    animation: ra-spin 0.7s linear infinite; margin: 0 auto;
  }
  @keyframes ra-spin { to { transform: rotate(360deg); } }
`;

let injected = false;
const injectCSS = () => {
  if (injected) return;
  injected = true;
  const el = document.createElement('style');
  el.innerHTML = CSS;
  document.head.appendChild(el);
};

/* ─── Delete Modal ───────────────────────────────────────────────────────── */
const DeleteModal = ({ isDark, reason, onClose, onConfirm, loading, success }) =>
  createPortal(
    <div
      className="ra-overlay"
      onClick={(e) => { if (!loading && !success && e.target === e.currentTarget) onClose(); }}
    >
      {success ? (
        <div className={`ra-card ${isDark ? 'dark-m' : ''}`}>
          <div className="ra-icon-wrap success-i"><IconCheck /></div>
          <p className="ra-title success-color">Deleted Successfully!</p>
          <p className="ra-sub">Cancel reason has been removed from the system.<br />Reloading…</p>
          <div className="ra-spin-green" />
        </div>
      ) : (
        <div className={`ra-card ${isDark ? 'dark-m' : ''}`}>

          <button className="ra-close" onClick={onClose} disabled={loading}>
            <IconClose />
          </button>

          <div className="ra-icon-wrap danger"><IconWarning /></div>

          <p className="ra-title">Delete Cancel Reason?</p>
          <p className="ra-sub">
            This will permanently remove the reason<br />from the system and cannot be undone.
          </p>

          {reason?.cancelreason && (
            <div>
              <span className="ra-badge">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {reason.cancelreason}
              </span>
            </div>
          )}

          <div className="ra-divider" />

          <div className="ra-actions">
            <button className="ra-cancel" onClick={onClose} disabled={loading}>Cancel</button>
            <button className="ra-confirm" onClick={onConfirm} disabled={loading}>
              {loading
                ? <><div className="ra-spin" /><span>Deleting…</span></>
                : <><IconTrash /><span>Delete</span></>}
            </button>
          </div>

        </div>
      )}
    </div>,
    document.body
  );

/* ─── Main Component ─────────────────────────────────────────────────────── */
export const ReasonActions = ({ cancelreason, allReasons }) => {
  injectCSS();

  const theme   = useTheme();
  const isDark  = theme.palette.mode === 'dark';
  const dispatch = useDispatch();

  const [show,    setShow]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const deletereason = async () => {
    setLoading(true);
    try {
      await dispatch(UpdateOne_CancelReasons_Data(cancelreason._id, { iscancelreasonactivate: 'false' }));
      setLoading(false);
      setSuccess(true);
      Notify('success', 'Deleted Successfully', 'success');
      setTimeout(() => {
        setShow(false);
        setSuccess(false);
        if (allReasons) allReasons();
        else window.location.reload();
      }, 1500);
    } catch {
      Notify('error', 'Failed to delete', 'error');
      setLoading(false);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Link
          to={`/updatecancelreason/${cancelreason._id}`}
          className={`ra-btn edit ${isDark ? 'dark-ra-edit' : ''}`}
          title="Edit reason"
        >
          <IconEdit />
        </Link>
        <button
          className={`ra-btn del ${isDark ? 'dark-ra-del' : ''}`}
          title="Delete reason"
          onClick={() => setShow(true)}
        >
          <IconTrash />
        </button>
      </div>

      {show && (
        <DeleteModal
          isDark={isDark}
          reason={cancelreason}
          onClose={() => { if (!loading && !success) setShow(false); }}
          onConfirm={deletereason}
          loading={loading}
          success={success}
        />
      )}
    </>
  );
};
